/**
 * IssueDetectionService.js
 * Service for detecting and reporting website issues such as broken links, script issues, and image optimization problems
 */

import scriptAnalyzerService from './ScriptAnalyzerService';
import imageAnalyzerService from './ImageAnalyzerService';
import linkCheckerService from './LinkCheckerService';

class IssueDetectionService {
  /**
   * Initialize the issue detection service
   * @param {string} apiBaseUrl - Base URL for the API
   */
  constructor(apiBaseUrl = null) {
    this.linkChecker = linkCheckerService;
    this.scriptAnalyzer = scriptAnalyzerService;
    this.imageAnalyzer = imageAnalyzerService;
    
    // Get API URL from environment or fallback to default
    const apiPort = process.env.REACT_APP_API_PORT || '5002';
    this.apiBaseUrl = apiBaseUrl || process.env.REACT_APP_API_URL || `http://localhost:${apiPort}/api`;
    this.requestTimeout = 60000; // 60 seconds timeout
    this.activeRequests = new Map(); // Track active requests
    console.log('Initializing IssueDetectionService with API base URL:', this.apiBaseUrl);
  }

  /**
   * Make a fetch request with timeout
   * @private
   * @param {string} url - The URL to fetch
   * @param {object} options - Fetch options
   * @param {number} timeout - Timeout in milliseconds
   * @returns {Promise<any>} - Response JSON
   */
  async fetchWithTimeout(url, options = {}, timeout = this.requestTimeout) {
    const requestKey = `${options.method || 'GET'}:${url}`;
    
    // Check if this exact request is already in progress
    if (this.activeRequests.has(requestKey)) {
      console.log(`Request to ${requestKey} already in progress, waiting...`);
      try {
        return await this.activeRequests.get(requestKey);
      } catch (error) {
        console.error(`Existing request to ${requestKey} failed:`, error);
        // If the existing request failed, try again with a new request
      }
    }
    
    // Create a new request with timeout
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    
    const fetchPromise = (async () => {
      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }
        
        return await response.json();
      } finally {
        clearTimeout(id);
        this.activeRequests.delete(requestKey);
      }
    })();
    
    // Store the promise
    this.activeRequests.set(requestKey, fetchPromise);
    
    return fetchPromise;
  }

  /**
   * Detect broken links on a URL
   * @param {string} url - The URL to analyze
   * @param {object} lighthouseData - Optional Lighthouse data if available
   * @returns {Promise<Object>} - Object containing broken links and stats
   */
  async detectBrokenLinks(url, lighthouseData = null) {
    try {
      console.log(`[IssueDetectionService] Detecting broken links for ${url}`);
      
      // Use client-side detection for broken links instead of server API
      console.log(`[IssueDetectionService] Using client-side link checker`);
      const data = await this.linkChecker.checkLinks(url);
      
      // Ensure data.links is always an array to avoid "brokenLinks.map is not a function" errors
      const brokenLinks = Array.isArray(data.links) ? data.links : [];
      
      console.log(`[IssueDetectionService] Client-side link check complete. Found ${brokenLinks.length} broken links.`);
      
      return {
        url,
        issues: brokenLinks,
        totalIssues: brokenLinks.length,
        timestamp: data.timestamp || new Date().toISOString()
      };
    } catch (error) {
      console.error(`[IssueDetectionService] Link detection failed: ${error.message}`);
      
      // If we have Lighthouse data, try to extract broken links
      if (lighthouseData) {
        console.log('[IssueDetectionService] Attempting to extract broken links from Lighthouse data');
        const brokenLinks = this._extractBrokenLinksFromLighthouse(lighthouseData);
        
        return {
          url,
          issues: brokenLinks,
          totalIssues: brokenLinks.length,
          timestamp: new Date().toISOString()
        };
      }
      
      return {
        url,
        issues: [],
        totalIssues: 0,
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Detect JavaScript and script issues on a website
   * @param {string} url - The URL to analyze
   * @param {object} lighthouseData - Optional Lighthouse data if available
   * @returns {Promise<Object>} - Detection results
   */
  async detectScriptIssues(url, lighthouseData = null) {
    try {
      console.log(`[IssueDetectionService] Detecting script issues for ${url}`);
      
      // Use client-side detection instead of server API
      console.log(`[IssueDetectionService] Using client-side script analyzer`);
      const data = await this.scriptAnalyzer.analyzeScripts(url);
      
      console.log(`[IssueDetectionService] Client-side script analysis complete. Found ${data.issues?.length || 0} script issues.`);
      
      return {
        url,
        scriptIssues: data.issues || [],
        totalIssues: data.issues?.length || 0,
        timestamp: data.timestamp || new Date().toISOString()
      };
    } catch (error) {
      console.error(`[IssueDetectionService] Script issue detection failed: ${error.message}`);
      
      // If we have Lighthouse data, try to extract script issues
      if (lighthouseData) {
        console.log('[IssueDetectionService] Attempting to extract script issues from Lighthouse data');
        const scriptIssues = this._extractScriptIssuesFromLighthouse(lighthouseData);
        
        return {
          url,
          scriptIssues: scriptIssues,
          totalIssues: scriptIssues.length,
          timestamp: new Date().toISOString()
        };
      }
      
      return {
        url,
        scriptIssues: [],
        totalIssues: 0,
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Detect image optimization issues on a website
   * @param {string} url - The URL to analyze
   * @param {object} lighthouseData - Optional Lighthouse data if available
   * @returns {Promise<Object>} - Detection results
   */
  async detectImageIssues(url, lighthouseData = null) {
    try {
      console.log(`[IssueDetectionService] Detecting image issues for ${url}`);
      
      // Use client-side detection instead of server API
      console.log(`[IssueDetectionService] Using client-side image analyzer`);
      const data = await this.imageAnalyzer.analyzeImages(url, 500 * 1024, 1000);
      
      console.log(`[IssueDetectionService] Client-side image analysis complete. Found ${data.issues?.length || 0} image issues.`);
      
      return {
        url,
        imageIssues: data.issues || [],
        totalIssues: data.issues?.length || 0,
        timestamp: data.timestamp || new Date().toISOString()
      };
    } catch (error) {
      console.error(`[IssueDetectionService] Image issue detection failed: ${error.message}`);
      
      // If we have Lighthouse data, try to extract image issues
      if (lighthouseData) {
        console.log('[IssueDetectionService] Attempting to extract image issues from Lighthouse data');
        const imageIssues = this._extractImageIssuesFromLighthouse(lighthouseData);
        
        return {
          url,
          imageIssues: imageIssues,
          totalIssues: imageIssues.length,
          timestamp: new Date().toISOString()
        };
      }
      
      return {
        url,
        imageIssues: [],
        totalIssues: 0,
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  }
  
  /**
   * Detect all issues on a URL
   * @param {string} url - URL to analyze
   * @param {Object} lighthouseData - Lighthouse data if available
   * @param {Object} options - Options
   * @returns {Promise<Object>} - All found issues
   */
  async detectAllIssues(url, lighthouseData, callbacks = {}) {
    const startTime = Date.now();
    let brokenLinks = [];
    let scriptIssues = [];
    let imageIssues = [];
    let errors = [];
    
    // Use a fallback for broken links from Lighthouse if available
    let lighthouseBrokenLinks = this._extractBrokenLinksFromLighthouse(lighthouseData);
    
    // Check for broken links
    try {
      if (callbacks?.onBrokenLinksStart) {
        callbacks.onBrokenLinksStart();
      }
      
      console.log('Checking for broken links...');
      const brokenLinksResult = await this.detectBrokenLinks(url);
      // Extract the actual broken links array from the result object
      brokenLinks = brokenLinksResult.issues || [];
      console.log(`Found ${brokenLinks.length} broken links`);
      
      if (callbacks?.onBrokenLinksComplete) {
        callbacks.onBrokenLinksComplete(brokenLinks);
      }
    } catch (error) {
      console.error('Error detecting broken links:', error);
      errors.push({ type: 'broken-links', message: error.message });
      
      // Use fallback from Lighthouse if available
      if (lighthouseBrokenLinks.length > 0) {
        console.log(`Using ${lighthouseBrokenLinks.length} broken links from Lighthouse data as fallback`);
        brokenLinks = lighthouseBrokenLinks;
      }
      
      if (callbacks?.onBrokenLinksComplete) {
        callbacks.onBrokenLinksComplete(brokenLinks, error);
      }
    }
    
    // Check for script issues
    try {
      if (callbacks?.onScriptIssuesStart) {
        callbacks.onScriptIssuesStart();
      }
      
      console.log('Checking for script issues...');
      const scriptIssuesResult = await this.detectScriptIssues(url);
      // Extract the actual script issues array from the result object
      scriptIssues = scriptIssuesResult.scriptIssues || [];
      console.log(`Found ${scriptIssues.length} script issues`);
      
      if (callbacks?.onScriptIssuesComplete) {
        callbacks.onScriptIssuesComplete(scriptIssues);
      }
    } catch (error) {
      console.error('Error detecting script issues:', error);
      errors.push({ type: 'script-issues', message: error.message });
      
      // Extract script issues from Lighthouse data as fallback
      const lighthouseScriptIssues = this._extractScriptIssuesFromLighthouse(lighthouseData);
      if (lighthouseScriptIssues.length > 0) {
        console.log(`Using ${lighthouseScriptIssues.length} script issues from Lighthouse data as fallback`);
        scriptIssues = lighthouseScriptIssues;
      }
      
      if (callbacks?.onScriptIssuesComplete) {
        callbacks.onScriptIssuesComplete(scriptIssues, error);
      }
    }
    
    // Check for image issues
    try {
      if (callbacks?.onImageIssuesStart) {
        callbacks.onImageIssuesStart();
      }
      
      console.log('Checking for image issues...');
      const imageIssuesResult = await this.detectImageIssues(url);
      // Extract the actual image issues array from the result object
      imageIssues = imageIssuesResult.imageIssues || [];
      console.log(`Found ${imageIssues.length} image issues`);
      
      if (callbacks?.onImageIssuesComplete) {
        callbacks.onImageIssuesComplete(imageIssues);
      }
    } catch (error) {
      console.error('Error detecting image issues:', error);
      errors.push({ type: 'image-issues', message: error.message });
      
      // Extract image issues from Lighthouse data as fallback
      const lighthouseImageIssues = this._extractImageIssuesFromLighthouse(lighthouseData);
      if (lighthouseImageIssues.length > 0) {
        console.log(`Using ${lighthouseImageIssues.length} image issues from Lighthouse data as fallback`);
        imageIssues = lighthouseImageIssues;
      }
      
      if (callbacks?.onImageIssuesComplete) {
        callbacks.onImageIssuesComplete(imageIssues, error);
      }
    }
    
    // Return combined results
    const endTime = Date.now();
    return {
      url,
      brokenLinks,
      scriptIssues,
      imageIssues,
      errors: errors.length > 0 ? errors : null,
      timestamp: new Date().toISOString(),
      totalTime: endTime - startTime,
      summary: {
        totalBrokenLinks: brokenLinks.length,
        totalScriptIssues: scriptIssues.length,
        totalImageIssues: imageIssues.length,
        totalIssues: brokenLinks.length + scriptIssues.length + imageIssues.length
      }
    };
  }

  /**
   * Extract broken links from Lighthouse data
   * @private
   * @param {object} lighthouseData - Lighthouse data
   * @returns {Array} - Broken links
   */
  _extractBrokenLinksFromLighthouse(lighthouseData) {
    try {
      const brokenLinks = [];
      
      // Get Lighthouse results
      const lighthouseResult = lighthouseData.lighthouseResult || lighthouseData;
      if (!lighthouseResult || !lighthouseResult.audits) return brokenLinks;
      
      // Check for broken links audit
      const brokenLinksAudit = lighthouseResult.audits['broken-links'];
      if (brokenLinksAudit && brokenLinksAudit.details && brokenLinksAudit.details.items) {
        brokenLinksAudit.details.items.forEach(item => {
          brokenLinks.push({
            url: item.url || item.source || '',
            statusCode: item.statusCode || 'Unknown',
            text: item.text || '',
            error: item.statusCode >= 400 ? `HTTP ${item.statusCode}` : null
          });
        });
      }
      
      return brokenLinks;
    } catch (error) {
      console.error('Error extracting broken links from Lighthouse data:', error);
      return [];
    }
  }

  /**
   * Extract script issues from Lighthouse data
   * @private
   * @param {object} lighthouseData - Lighthouse data
   * @returns {Array} - Script issues
   */
  _extractScriptIssuesFromLighthouse(lighthouseData) {
    try {
      const scriptIssues = [];
      
      // Get Lighthouse results
      const lighthouseResult = lighthouseData.lighthouseResult || lighthouseData;
      if (!lighthouseResult || !lighthouseResult.audits) return scriptIssues;
      
      // List of relevant audit IDs
      const relevantAudits = [
        'js-errors',
        'js-libraries-with-security-vulnerabilities',
        'third-party-summary',
        'bootup-time',
        'mainthread-work-breakdown',
        'critical-request-chains'
      ];
      
      // Process each relevant audit
      for (const auditId of relevantAudits) {
        const audit = lighthouseResult.audits[auditId];
        if (audit && audit.details && audit.details.items) {
          audit.details.items.forEach(item => {
            scriptIssues.push({
              type: auditId,
              url: item.source || item.url || item.scriptUrl || '',
              description: item.description || audit.description || '',
              severity: audit.score === null ? 'medium' : audit.score < 0.5 ? 'high' : 'low'
            });
          });
        }
      }
      
      return scriptIssues;
    } catch (error) {
      console.error('Error extracting script issues from Lighthouse data:', error);
      return [];
    }
  }

  /**
   * Extract image issues from Lighthouse data
   * @private
   * @param {object} lighthouseData - Lighthouse data
   * @returns {Array} - Image issues
   */
  _extractImageIssuesFromLighthouse(lighthouseData) {
    try {
      const imageIssues = [];
      
      // Get Lighthouse results
      const lighthouseResult = lighthouseData.lighthouseResult || lighthouseData;
      if (!lighthouseResult || !lighthouseResult.audits) return imageIssues;
      
      // List of relevant audit IDs
      const relevantAudits = [
        'uses-responsive-images',
        'uses-optimized-images',
        'offscreen-images',
        'unsized-images',
        'uses-webp-images'
      ];
      
      // Process each relevant audit
      for (const auditId of relevantAudits) {
        const audit = lighthouseResult.audits[auditId];
        if (audit && audit.details && audit.details.items) {
          audit.details.items.forEach(item => {
            imageIssues.push({
              type: auditId,
              url: item.url || '',
              description: audit.description || '',
              size: item.totalBytes || 0,
              width: item.width || 0,
              height: item.height || 0,
              wastedBytes: item.wastedBytes || 0
            });
          });
        }
      }
      
      return imageIssues;
    } catch (error) {
      console.error('Error extracting image issues from Lighthouse data:', error);
      return [];
    }
  }
}

// Create a singleton instance
const instance = new IssueDetectionService();
export default instance; 